import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PaEmptyState,
  PaFilterBar,
  PaPageHeader,
  PaPagination,
  PaSkeleton,
} from "components/platform-admin";
import PlatformAdminLayout from "pages/PlatformAdmin/layout/PlatformAdminLayout";
import PaAccessDenied from "pages/PlatformAdmin/layout/PaAccessDenied";
import { platformRoleAccess } from "helpers/RolesPermission";
import { getPaginationObj } from "Utils/utils";
import {
  exportPlatformUsageCsv,
  fetchPlatformUsageHeatmap,
  refreshAllPlatformUsage,
} from "store/services/platformAdmin.services";
import { URL_PLATFORM_ADMIN_USAGE } from "store/services/URL";
import {
  buildUsageHeatmapColumns,
  buildUsageListQuery,
  DEFAULT_USAGE_FILTERS,
  formatCareSector,
  formatUsageSnapshotMeta,
  formatUsageTimestamp,
  resolveUsageCell,
  USAGE_SCORE_LEGEND,
} from "./usageOverview.utils";

const UsageOverview = () => {
  const canViewUsage = platformRoleAccess("platform.usage.view");
  const canExportUsage = platformRoleAccess("platform.usage.export");
  const canRefreshUsage = platformRoleAccess("platform.usage.refresh");
  const [filters, setFilters] = useState(DEFAULT_USAGE_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);
  const [pagination, setPagination] = useState(getPaginationObj());
  const [exporting, setExporting] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);

  const loadUsage = useCallback(async () => {
    if (!canViewUsage) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endpoint = buildUsageListQuery(filters, URL_PLATFORM_ADMIN_USAGE);
      const data = await fetchPlatformUsageHeatmap(endpoint);
      setUsage(data);
      setPagination(getPaginationObj(data?.tenants, filters));
    } catch (err) {
      setUsage(null);
      setError(err?.message || "Failed to load usage heatmap");
    } finally {
      setLoading(false);
    }
  }, [canViewUsage, filters]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search === searchInput) {
          return prev;
        }

        return { ...prev, search: searchInput, page: 1 };
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const columns = useMemo(
    () => buildUsageHeatmapColumns(usage?.modules),
    [usage?.modules]
  );

  const rows = useMemo(() => {
    const tenants = usage?.tenants;
    return Array.isArray(tenants?.data) ? tenants.data : [];
  }, [usage?.tenants]);

  const snapshotMeta = usage?.meta;
  const needsSnapshotRefresh = (snapshotMeta?.tenants_with_snapshots ?? 0) === 0;

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPlatformUsageCsv();
    } catch (err) {
      setError(err?.message || "Failed to export usage CSV");
    } finally {
      setExporting(false);
    }
  };

  const handleRefreshAll = async () => {
    setRefreshingAll(true);
    setError(null);

    try {
      const result = await refreshAllPlatformUsage();
      toast.success(`Refreshed ${result?.refreshed ?? 0} tenant snapshots`);
      await loadUsage();
    } catch (err) {
      const message = err?.message || "Failed to refresh usage snapshots";
      setError(message);
      toast.error(message);
    } finally {
      setRefreshingAll(false);
    }
  };

  if (!canViewUsage) {
    return (
      <PaAccessDenied
        title="Usage overview"
        subtitle="Tenant adoption and feature usage"
        description="You need platform.usage.view."
      />
    );
  }

  return (
    <PlatformAdminLayout>
      <PaPageHeader
        title="Feature usage"
        subtitle={formatUsageSnapshotMeta(snapshotMeta)}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canRefreshUsage ? (
              <button
                type="button"
                onClick={handleRefreshAll}
                disabled={refreshingAll || loading}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {refreshingAll ? "Refreshing all…" : "Refresh all snapshots"}
              </button>
            ) : null}
            {canExportUsage ? (
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting || needsSnapshotRefresh}
                title={needsSnapshotRefresh ? "Refresh snapshots before exporting scores" : undefined}
                className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
            ) : null}
          </div>
        }
      />

      {needsSnapshotRefresh && !loading ? (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Scores are blank because no nightly snapshot has run yet.
          {canRefreshUsage ? " Click “Refresh all snapshots” to compute adoption scores for every tenant." : " Ask a platform admin with refresh permission to run a snapshot refresh."}
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-3 text-xs text-gray-600">
        <span className="font-medium text-gray-700">Score legend:</span>
        {USAGE_SCORE_LEGEND.map((item) => (
          <span key={item.label} className="inline-flex items-center gap-1.5">
            <span className={`rounded px-2 py-0.5 font-semibold ${item.className}`}>{item.label}</span>
          </span>
        ))}
        <span className="text-gray-400">· N/A = module not applicable · — = no snapshot</span>
      </div>

      <PaFilterBar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search tenant"
        onClear={searchInput ? () => {
          setSearchInput("");
          setFilters(DEFAULT_USAGE_FILTERS);
        } : undefined}
        resultCount={pagination.total}
      />

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6">
            <PaSkeleton variant="table" lines={6} />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6">
            <PaEmptyState title="No tenants match" message="Try a different search or clear filters." />
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Tenant</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Sector</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Snapshot</th>
                {columns.map((column) => (
                  <th key={column.key} className="px-3 py-3 text-center font-semibold text-gray-700">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => (
                <tr key={row.sp_id} className="hover:bg-indigo-50/40">
                  <td className="px-4 py-3">
                    <Link to={`/tenants/${row.sp_id}`} className="font-medium text-indigo-600 hover:text-indigo-700">
                      {row.name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatCareSector(row.care_sector)}</td>
                  <td className="px-4 py-3 text-gray-500">
                    {row.has_snapshot ? formatUsageTimestamp(row.computed_at) : "—"}
                  </td>
                  {columns.map((column) => {
                    const cell = resolveUsageCell(row.scores?.[column.key], row.has_snapshot);

                    return (
                      <td key={column.key} className="px-3 py-3 text-center">
                        <span
                          title={cell.title}
                          className={`inline-flex min-w-[2.5rem] justify-center rounded-md px-2 py-1 text-xs font-semibold ${cell.className}`}
                        >
                          {cell.display}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PaPagination
        page={filters.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        rows={rows}
        perPage={filters.perPage}
        loading={loading}
        onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
      />
    </PlatformAdminLayout>
  );
};

export default UsageOverview;
